import { FC, Fragment, JSX, useMemo, useState } from 'react';
import { DataTable } from '@consolidocs-ui/components';
import { PageBreadcrumbs } from '@consolidocs-ui/components';
import { useNavigate } from 'react-router';
import { IndustryType } from '@consolidocs-ui/types';
import SearchIcon from '@mui/icons-material/Search';
import { useGetIndustriesQuery } from '@consolidocs-ui/data-access/IndustryApi';
import { RowMenu } from './RowMenu';
import { Button, FormControl, FormLabel, Input, Sheet } from '@mui/joy';
import { ContractType } from '@consolidocs-ui/types';

export const AllIndustries: FC = (): JSX.Element => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const { data: industriesList, isLoading } = useGetIndustriesQuery();

  const industries = useMemo(
    () => industriesList?.data || [],
    [industriesList]
  );

  const totalPages = Math.max(1, Math.ceil(industries.length / 10));

  const filteredIndustries = useMemo(() => {
    const lowerSearch = searchQuery.trim().toLowerCase();

    return industries
      .filter((industry) => {
        return (
          industry?.name?.toLowerCase().includes(lowerSearch) ||
          industry?.code?.toLowerCase().includes(lowerSearch) ||
          industry?.description?.toLowerCase().includes(lowerSearch)
        );
      })
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
  }, [industries, searchQuery]);

  const paginatedIndustries = useMemo(() => {
    const start = (page - 1) * 10;
    return filteredIndustries.slice(start, start + 10);
  }, [page, filteredIndustries]);

  return (
    <Fragment>
      <Sheet
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          backgroundColor: '#F0F4F8',
          mb: 1,
        }}
      >
        <PageBreadcrumbs
          items={[
            {
              label: 'All Industries',
            },
          ]}
        />
        <Button onClick={() => navigate('/industries/create')}>
          Create Industry
        </Button>
      </Sheet>

      <FormControl sx={{ flex: 1, mb: 2 }} size="sm">
        <FormLabel>Search for Industries</FormLabel>
        <Input
          size="sm"
          placeholder="Search"
          startDecorator={<SearchIcon />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </FormControl>

      <DataTable<IndustryType>
        data={paginatedIndustries}
        isLoading={isLoading}
        page={page}
        setPage={setPage}
        totalPages={totalPages}
        selectedKeys={selected}
        onSelectChange={setSelected}
        getRowKey={(item) => item?.id || ''}
        onRowClick={(item) => {
          if (!item?.id) return;
          navigate(`/industries/edit/${item?.id}`);
        }}
        columns={[
          { key: '__select__', align: 'center' },
          { key: 'id', label: 'ID', align: 'left' },
          { key: 'code', label: 'Code', align: 'left' },
          {
            key: 'name',
            label: 'Name',
            align: 'center',
          },
          { key: 'description', label: 'Description', align: 'center' },
          { key: 'contract_types', label: 'Contract Types', align: 'center' },
          { key: 'actions', label: 'Actions', align: 'center' },
        ]}
        children={{
          actions: ({ item }) => (
            <RowMenu
              industryId={item?.id || ''}
              onClick={(e) => e.stopPropagation()}
            />
          ),
          contract_types: ({ item }) =>
            item?.contract_types
              ?.map((type: ContractType) => type.name)
              .join(', ') || 'N/A',
        }}
      />
    </Fragment>
  );
};
